import { Space, Tag } from 'antd';
import { useCallback, useEffect } from 'react';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import { MarkupTypeEnum, PartialMarkup } from 'domains/markupDomain';
import { RevxButton } from 'components/RevxButton';

const OverrideTable = (props: { mode: 'global' | 'revshop' }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const isGlobal = props.mode === 'global';

  // table data processing
  const data = useAppSelector((state) => {
    if (isGlobal && state.markup.globalOverrideMarkups?.content) {
      return state.markup.globalOverrideMarkups.content.map((m) => {
        return {
          ...m,
          markupPct: m.markupRanges[0].markupPct,
        } as PartialMarkup;
      });
    } else if (!isGlobal && state.markup.revshopOverrideMarkups?.content) {
      return state.markup.revshopOverrideMarkups.content.map((m) => {
        return {
          ...m,
          markupPct: m.markupRanges[0].markupPct,
        } as PartialMarkup;
      });
    } else {
      return [];
    }
  });
  const total = useAppSelector((state) =>
    isGlobal
      ? state.markup.globalOverrideMarkups?.totalElements || 0
      : state.markup.revshopOverrideMarkups?.totalElements || 0
  );
  const currentPage = useAppSelector((state) =>
    isGlobal
      ? state.markup.globalOverrideCurrentPage
      : state.markup.revshopOverrideCurrentPage
  );
  const pageSize = useAppSelector((state) =>
    isGlobal
      ? state.markup.globalOverridePageSize
      : state.markup.revshopOverridePageSize
  );
  const loading = useAppSelector(
    (state) =>
      state.loading.effects.markup.getGlobalOverrideMarkups ||
      state.loading.effects.markup.getRevshopOverrideMarkups
  );

  useEffect(() => {
    if (isGlobal) {
      dispatch.markup.getGlobalOverrideMarkups({
        page: { currentPage, pageSize },
        query: { type: MarkupTypeEnum.GLOBAL_OVERRIDE },
      });
    } else {
      dispatch.markup.getRevshopOverrideMarkups({
        page: { currentPage, pageSize },
        query: { type: MarkupTypeEnum.REVSHOP_OVERRIDE },
      });
    }
  }, [currentPage, pageSize, dispatch.markup, isGlobal]);

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          const currentPage = pagination.current;
          const pageSize = pagination.pageSize;
          dispatch.markup.setState(
            isGlobal
              ? {
                  globalOverrideCurrentPage: currentPage,
                  globalOverridePageSize: pageSize,
                }
              : {
                  revshopOverrideCurrentPage: currentPage,
                  revshopOverridePageSize: pageSize,
                }
          );
          break;
      }
    },
    [dispatch.markup, isGlobal]
  );
  const columns: TableColumnsType<PartialMarkup> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      fixed: 'left',
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      width: 200,
    },
    {
      title: t('platform.markup.columns.markupPct'),
      dataIndex: 'markupPct',
      valueType: 'percentage',
    },
    {
      title: t('platform.markup.columns.startDate'),
      dataIndex: 'startDt',
    },
    {
      title: t('platform.markup.columns.endDate'),
      dataIndex: 'endDt',
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'createAt',
      valueType: 'time',
    },
    {
      title: t('common.active'),
      fixed: 'right',
      dataIndex: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            onClick={() =>
              dispatch.markup.setState({
                curOverrideMarkup: {
                  ...record,
                },
              })
            }
          >
            {t('common.view')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  return (
    <RevxTable<PartialMarkup>
      loading={loading}
      columns={columns}
      dataSource={data}
      pagination={{
        pageSize,
        total,
      }}
      onChange={onTableChange}
    />
  );
};

export default OverrideTable;
